import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import AppNavPublic from '@/components/layout/AppNavPublic';

// ─────────────────────────────────────────────
// MESH GRADIENT (identical to HeroSection)
// ─────────────────────────────────────────────
const MeshGradient = () => (
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      zIndex: 0,
      background: 'linear-gradient(87deg, #00022e 0%, #02075d 100%)',
    }}
  />
);

// ─────────────────────────────────────────────
// CALLOUT BOX (must be defined before FAQ_GROUPS which references it)
// ─────────────────────────────────────────────
const Callout = ({ children }) => (
  <div
    className="flex gap-3.5 items-start rounded-xl p-3.5 mt-1 text-sm leading-relaxed"
    style={{
      background: 'rgba(51,153,153,0.07)',
      border: '1px solid rgba(51,153,153,0.2)',
      color: 'rgba(255,255,255,0.68)',
    }}
  >
    <svg className="flex-shrink-0 mt-0.5" style={{ color: '#339999' }} width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="7"/>
      <path d="M8 5v3.5M8 11v.5" strokeLinecap="round"/>
    </svg>
    {children}
  </div>
);

// ─────────────────────────────────────────────
// FAQ DATA
// ─────────────────────────────────────────────
const FAQ_GROUPS = [
  {
    label: 'vs. Other Tools',
    items: [
      {
        q: 'How is this different from Google Drive + DocuSign?',
        a: (
          <div className="space-y-3">
            <p>Google Drive stores files. DocuSign gets signatures. Neither one <strong className="text-white/90 font-semibold">proves compliance.</strong></p>
            <p>When the auditor asks: <em>"Which version was approved?", "Can you prove all 47 staff acknowledged it?", "When was this last reviewed?"</em> — Google Drive can't answer in 30 seconds. Veracord can.</p>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 text-white/30 font-semibold uppercase tracking-widest border-b border-white/5"></th>
                    <th className="text-left p-2 text-white/30 font-semibold uppercase tracking-widest border-b border-white/5">Google Drive + DocuSign</th>
                    <th className="text-left p-2 text-white/30 font-semibold uppercase tracking-widest border-b border-white/5">Veracord</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Immutable version control', false, true],
                    ['Acknowledgment tracking with timestamps', false, true],
                    ['Audit export in under 5 minutes', false, true],
                    ['Built specifically for clinic audits', false, true],
                  ].map(([label, gd, vc]) => (
                    <tr key={label} className="border-b border-white/[0.04]">
                      <td className="p-2 text-white/55">{label}</td>
                      <td className="p-2"><span style={{ color: '#f08080' }}>✗</span></td>
                      <td className="p-2 text-white/90"><span style={{ color: '#339999' }}>✓</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Callout>We're not replacing Google Drive. We're replacing the two weeks of hell that happens when the auditor shows up.</Callout>
          </div>
        ),
      },
      {
        q: 'Why not just use ChatGPT to write policies?',
        a: (
          <div className="space-y-3">
            <p>Three reasons:</p>
            <ul className="space-y-2">
              {[
                ['ChatGPT hallucinates', "it'll confidently cite regulations that don't exist. Policy Builder only references documents the clinic uploads."],
                ['No accountability trail', "an auditor can't verify where the language came from. Our system shows exactly which source document informed each section."],
                ['No version control', "ChatGPT is just a drafting tool. Veracord creates the immutable, timestamped record that stands up in an audit."],
              ].map(([bold, rest]) => (
                <li key={bold} className="flex gap-3.5">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#339999', marginTop: '7px' }} />
                  <span><strong className="text-white/88 font-semibold">{bold}</strong> — {rest}</span>
                </li>
              ))}
            </ul>
            <Callout>Drafting gets you started in minutes. Veracord makes it defensible forever.</Callout>
          </div>
        ),
      },
      {
        q: 'Why not use PowerDMS, Compli, or another compliance platform?',
        a: (
          <div className="space-y-3">
            <p>Those platforms were built for hospital systems and Fortune 500 compliance teams — not independent clinics. They come with implementation timelines measured in months, pricing starting at $500–$2,000/month, and features you'll never use.</p>
            <p>Veracord is built for the 10–50 person clinic where the compliance officer might also be the office manager. Setup under an hour. $249/month. One job: make you audit-ready.</p>
          </div>
        ),
      },
    ],
  },
  {
    label: 'Getting Started',
    items: [
      {
        q: 'How long does it take to get set up?',
        a: (
          <div className="space-y-3">
            <p>Most clinics are fully onboarded in under an hour. Upload your existing policies, invite your staff, and Veracord handles the rest — acknowledgment requests, review scheduling, version locking. No IT required.</p>
            <p>If you have 10 policies and 20 staff, you can be audit-ready before lunch.</p>
          </div>
        ),
      },
      {
        q: 'What if we already have policies written somewhere else?',
        a: (
          <div className="space-y-3">
            <p>That's the most common starting point. Upload your existing policies, approve them, and they become your locked record from that point forward.</p>
            <p>Policy Builder is optional — useful if you want to modernize outdated language or align with recent regulatory changes. But plenty of clinics just bring their existing policies over and immediately become audit-ready.</p>
            <Callout>Your existing work isn't wasted — it becomes the foundation of your defensible record.</Callout>
          </div>
        ),
      },
      {
        q: 'Do staff need training to use Veracord?',
        a: (
          <div className="space-y-3">
            <p>No. Staff log in, see the policies they need to acknowledge, click to acknowledge, and they're done. There's no learning curve because there's nothing to learn — it's one action.</p>
            <p>Admins have a bit more to navigate, but the dashboard shows exactly what needs attention today and nothing else. If you can use email, you can use Veracord.</p>
          </div>
        ),
      },
      {
        q: 'What types of clinics is Veracord built for?',
        a: (
          <div className="space-y-3">
            <p>Any outpatient healthcare setting with compliance obligations — primary care, dental, specialty practices, mental health clinics, physical therapy, urgent care.</p>
            <p>We're focused on independent and mid-sized clinics (typically 5–100 staff). If you have staff who need to acknowledge policies and an auditor who will eventually ask for proof, Veracord is built for you.</p>
          </div>
        ),
      },
    ],
  },
  {
    label: 'Audits & Proof',
    items: [
      {
        q: 'What does the audit export actually look like?',
        a: (
          <div className="space-y-3">
            <p>A complete, timestamped package — not a dashboard screenshot. You get a PDF with:</p>
            <ul className="space-y-2">
              {[
                'Every current policy (locked, read-only)',
                'Full version history with change logs',
                'Staff acknowledgment table with names, roles, and timestamps',
                'Review schedule with ownership assignments',
              ].map(item => (
                <li key={item} className="flex gap-3.5">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#339999', marginTop: '7px' }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>Designed to hand directly to an auditor without explanation. We show you a sample on every demo call.</p>
          </div>
        ),
      },
      {
        q: "What if we don't get audited that often?",
        a: (
          <div className="space-y-3">
            <p>That's exactly the problem. When the audit finally happens, you're not ready — because you only think about it once a year. The scramble is proportional to how long it's been since the last one.</p>
            <p>Veracord keeps you audit-ready every single day, automatically. When it happens — and it will — you're done in 30 seconds instead of two weeks.</p>
          </div>
        ),
      },
      {
        q: 'What happens when compliance requirements change?',
        a: (
          <div className="space-y-3">
            <p>That's exactly when clinics need Veracord most. Upload the updated regulatory document to Policy Builder, review the suggested changes, approve and lock the new version, and staff get notified automatically.</p>
            <Callout>We don't just handle compliance — we handle compliance changes. In healthcare, that happens constantly.</Callout>
          </div>
        ),
      },
    ],
  },
  {
    label: 'Product & Security',
    items: [
      {
        q: 'Is our policy data secure?',
        a: (
          <div className="space-y-3">
            <p>All policies are stored with encryption at rest and in transit. Access is role-based — staff see only what they need to acknowledge, admins manage policies, and owners have full access.</p>
            <p>The immutability that makes Veracord audit-proof also means no one can alter or delete records after approval — not even us. Your compliance record is yours, locked, and permanent.</p>
          </div>
        ),
      },
      {
        q: 'How much does Veracord cost?',
        a: (
          <div className="space-y-3">
            <p>$249/month per clinic — unlimited policies, unlimited staff, unlimited exports, Policy Builder included. Or $2,490/year — that's two months free. No per-user fees, no setup costs, no feature tiers.</p>
            <Callout>$249/month is less than what most clinics spend in coffee during two weeks of audit scramble. First time you avoid that nightmare, it pays for itself many times over.</Callout>
          </div>
        ),
      },
    ],
  },
];


// ─────────────────────────────────────────────
// ACCORDION ITEM
// ─────────────────────────────────────────────
const AccordionItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.23, 1, 0.32, 1] }}
      className="rounded-2xl overflow-hidden"
      style={{
        border: open ? '1.5px solid rgba(51,153,153,0.4)' : '1.5px solid rgba(255,255,255,0.08)',
        background: open ? 'rgba(51,153,153,0.05)' : 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        transition: 'border-color 0.25s, background 0.25s',
        boxShadow: open ? '0 8px 40px rgba(51,153,153,0.08)' : 'none',
      }}
    >
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 text-left"
        style={{ padding: '24px 24px' }}
      >
        <div className="flex items-start gap-5">
          {/* Q chip */}
          <div
            className="flex-shrink-0 flex items-center justify-center rounded-lg"
            style={{
              width: '30px', height: '30px', marginTop: '1px',
              background: 'rgba(51,153,153,0.15)',
              border: '1px solid rgba(51,153,153,0.25)',
              fontSize: '10px', fontWeight: 800,
              color: '#5ecfcf', letterSpacing: '0.03em',
            }}
          >
            Q
          </div>
          <span
            className="font-semibold leading-snug"
            style={{ fontSize: '15px', color: 'rgba(255,255,255,0.92)', paddingTop: '4px' }}
          >
            {q}
          </span>
        </div>

        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
          style={{ flexShrink: 0, color: open ? '#5ecfcf' : 'rgba(255,255,255,0.3)' }}
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </button>

      {/* Panel */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div
              style={{
                padding: '0 24px 24px 66px',
                fontSize: '14.5px',
                lineHeight: '1.78',
                color: 'rgba(255,255,255,0.58)',
              }}
            >
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─────────────────────────────────────────────
// FAQ GROUP
// ─────────────────────────────────────────────
const FaqGroup = ({ label, items, groupIndex }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay: groupIndex * 0.08, ease: [0.23, 1, 0.32, 1] }}
    className="mb-20"
  >
    {/* Section label */}
    <p
      className="mb-5 pl-0.5 mt-3"
      style={{
        fontSize: '16px', fontWeight: 700,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        color: '#339999',
      }}
    >
      {label}
    </p>

    <div className="flex flex-col gap-0.5">
      {items.map((item, i) => (
        <AccordionItem key={i} q={item.q} a={item.a} index={i} />
      ))}
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────
// FAQ PAGE
// ─────────────────────────────────────────────
export const FAQSection = () => {
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className="relative min-h-screen w-full text-white"
      style={{ fontFamily: 'Inter, sans-serif', background: '#00022e' }}
    >
      <MeshGradient />

      {/* ── Nav ── */}
      <AppNavPublic activeItem="FAQ" fixed scrolled={navScrolled} />

      {/* ── Main Content ── */}
      <main
        className="relative max-w-3xl mx-auto px-6 md:px-8"
        style={{ zIndex: 1, paddingTop: '140px', paddingBottom: '100px' }}
      >

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
          className="text-center mb-16"
        >
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full"
            style={{
              background: 'rgba(51,153,153,0.12)',
              border: '1px solid rgba(51,153,153,0.3)',
              fontSize: '11px', fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              color: '#5ecfcf',
            }}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span
                className="faq-badge-ping absolute inline-flex h-full w-full rounded-full"
                style={{ backgroundColor: '#339999' }}
              />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: '#339999' }} />
            </span>
            Common Questions
          </div>

          {/* Headline */}
          <h1
            className="mb-0"
            style={{
              fontFamily: 'Source Serif Pro, Georgia, serif',
              fontSize: 'clamp(40px, 6vw, 62px)',
              fontWeight: 700,
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              color: '#fff',
            }}
          >
            Everything you need
            <br />
            <span style={{
              background: 'linear-gradient(90deg, #339999 0%, #ffffff 40%, #339999 80%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'gradientSweep 3.5s linear infinite',
            }}>
              to make the call.
            </span>
          </h1>

          {/* Teal underline accent */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
            style={{
              height: '3px', width: '48px', borderRadius: '99px',
              background: 'linear-gradient(90deg, #339999, #06b6d4)',
              margin: '20px auto 22px',
              transformOrigin: 'left center',
            }}
          />

          <p
            className="max-w-md mx-auto"
            style={{ fontSize: '17px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 }}
          >
            Real answers to the questions compliance officers ask before they sign up.
          </p>
        </motion.div>

        {/* FAQ Groups */}
        {FAQ_GROUPS.map((group, i) => (
          <FaqGroup key={group.label} label={group.label} items={group.items} groupIndex={i} />
        ))}

        {/* ── CTA Block (matches FinalCtaSection tone) ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          className="relative overflow-hidden rounded-3xl mt-16 text-center"
          style={{
            border: '1.5px solid rgba(51,153,153,0.3)',
            background: 'linear-gradient(135deg, rgba(51,153,153,0.08), rgba(2,7,93,0.5))',
            padding: '56px 48px',
          }}
        >
          {/* Top teal accent line */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: 'linear-gradient(90deg, #339999, #06b6d4, rgba(6,182,212,0))',
          }} />

          <h2 style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 'clamp(22px, 4vw, 38px)',
            fontWeight: 900, letterSpacing: '-0.03em',
            lineHeight: 1.1, color: '#fff', marginBottom: '14px',
            textWrap: 'balance',
          }}>
            Still have questions?
            <span style={{ color: '#339999', display: 'block', marginTop: '4px' }}>We're here.</span>
          </h2>

          <p style={{
            fontSize: '15px', color: 'rgba(255,255,255,0.5)',
            lineHeight: 1.65, maxWidth: '400px',
            margin: '0 auto 36px',
          }}>
            Send us a message and we'll get back to you — usually within one business day. As we grow, we'll add live chat and demo scheduling directly here.
          </p>

          <motion.a
            href="mailto:hello@veracord.co"
            whileHover={{ scale: 1.03, boxShadow: '0 8px 40px rgba(51,153,153,0.45)' }}
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="inline-flex items-center gap-2.5 text-white no-underline"
            style={{
              padding: '16px 32px', borderRadius: '12px',
              background: 'linear-gradient(135deg, #339999, #0d9488)',
              fontWeight: 800, fontSize: '16px',
              letterSpacing: '-0.01em',
              boxShadow: '0 4px 24px rgba(51,153,153,0.3)',
            }}
          >
            Send Us a Message
            <ArrowRight className="w-4 h-4" />
          </motion.a>

          <p style={{
            marginTop: '14px', fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: 500,
            color: 'rgba(255,255,255,0.3)',
            display: 'flex', alignItems: 'start',
            justifyContent: 'center', gap: '6px',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(51,153,153,0.6)" strokeWidth="2.5">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            No sales pressure · No commitment · Built for healthcare clinics
          </p>
        </motion.div>

        {/* Footer line */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="flex items-center justify-between flex-wrap gap-3 mt-16 pt-8"
          style={{ borderTop: '1px solid rgba(51,153,153,0.15)' }}
        >
          <span style={{
            fontFamily: 'Source Serif Pro, Georgia, serif',
            fontWeight: 700, fontSize: '15px',
            color: 'rgba(255,255,255,0.4)',
            letterSpacing: '-0.01em',
          }}>
            Veracord<span style={{ fontSize: '9px', fontWeight: 400, position: 'relative', top: '-8px', marginLeft: '1px', letterSpacing: 0, fontFamily: 'Inter, sans-serif' }}>™</span>
          </span>
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px', color: 'rgba(255,255,255,0.2)',
            letterSpacing: '0.05em',
          }}>
            The System of Record
          </span>
        </motion.div>

      </main>
    </div>
  );
};

export default FAQSection;
